import { Box, IconButton, SvgIcon, Button, Typography } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import { Select } from "components";
import { styled } from "@mui/material/styles";
import { ReactComponent as arrowIcon } from "assets/icons/icon-arrow.svg";
import { first, last } from "lodash";
import { useTranslation } from "react-i18next";
import { useFocusStyles } from "hooks/focusBorder";
import portalSettingsManager from "utils/portalSettingsManager";
import { useEffect, useRef, useState } from "react";

const Span = styled("span")(() => ({
  fontSize: "12px",
  color: `#595959`,
  lineHeight: "14px",
  padding: "0 14px",
  position: "relative",
  top: "-1px",
  cursor: "pointer",
  "&.selected": {
    // bug 153660 accessibility contrast ratio bug.
    color: `#122674`,
  },
}));

const MobileButton = withStyles((theme) => ({
  root: {
    borderRadius: "0px",
    borderWidth: "1px",
    borderColor: `${portalSettingsManager.buttonColors?.themed?.active?.border || theme.palette.primary.main}`,
    color: portalSettingsManager.buttonColors.themed.active.text,
    "&:disabled": { color: theme.palette.grey["800"] },
    margin: "10px 10px",
  },
  label: {
    fontSize: "11px",
    fontWeight: "bold",
  },
}))(Button);

export const Paginator = ({ pages, currentPage, onChange, isMobile }: any): React.ReactElement => {
  const pageLimit = 10;
  const { t } = useTranslation();
  const currentPageRef = useRef(null);
  const [prevPage, setPrevPage] = useState(currentPage);

  const handlePrevClick = () => {
    setPrevPage(currentPage);
    onChange(currentPage - 1);
  };
  const handleNextClick = () => {
    setPrevPage(currentPage);
    onChange(currentPage + 1);
  };

  useEffect(() => {
    // Only focus if the page has changed via Next/Prev buttons
    if (currentPage !== prevPage) {
      if (currentPage === first(pages) || currentPage === last(pages)) {
        if (isMobile) {
          const mobileDropdown: HTMLElement | null = document.querySelector(
            `#btnfilter-${currentPage}`,
          );
          if (mobileDropdown) {
            mobileDropdown.focus();
          }
        } else currentPageRef.current?.focus();
      }
    }
  }, [currentPage, prevPage, isMobile]);

  const getPaginationGroup = () => {
    const start = Math.floor((currentPage - 1) / pageLimit) * pageLimit;
    let end = start + pageLimit;
    if (end > pages.length) {
      end = pages.length;
    }
    const pageArray = [];
    for (let i = start; i < end; i++) {
      pageArray.push(pages[i]);
    }
    return pageArray;
  };
  const focusClass = useFocusStyles();
  return (
    <Box display="flex" justifyContent="center" alignItems="center">
      {isMobile ? (
        <MobileButton
          aria-label={t("aria_label_go_to_previous")}
          onClick={() => handlePrevClick()}
          variant={currentPage !== first(pages) ? "outlined" : undefined}
          disabled={currentPage === first(pages)}
          className={focusClass.focusItem}
          aria-disabled={currentPage === first(pages)}
        >
          {t("paginator_prev")}
        </MobileButton>
      ) : (
        <IconButton
          aria-label={t("aria_label_go_to_previous")}
          color="primary"
          onClick={() => handlePrevClick()}
          disabled={currentPage === first(pages)}
          tabIndex={0}
          className={focusClass.focusItem}
          aria-disabled={currentPage === first(pages)}
          size="large"
        >
          <SvgIcon
            component={arrowIcon}
            style={{ transform: "rotate(180deg) translate(25%, 30%)" }}
          />
        </IconButton>
      )}
      {isMobile ? (
        <Box
          display={{ xs: "flex", sm: "flex", md: "flex" }}
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-start"
          marginLeft={{ xs: "10px", sm: "0px" }}
        >
          <Box marginRight={{ xs: "10px", sm: "10px" }}>
            <Typography style={{ fontSize: "12px" }}>Page</Typography>
          </Box>
          <Select
            title={`${currentPage}`}
            options={pages.map((page: any) => {
              return { title: page, id: page };
            })}
            value={currentPage}
            onChange={onChange || { title: currentPage, id: currentPage }}
            pagenation={true}
            buttonLabelId={"page"}
          />
        </Box>
      ) : (
        getPaginationGroup().map((page: number) => (
          <Span
            key={page}
            className={currentPage === page ? "selected" : ""}
            onClick={() => onChange(page)}
            role="link"
            aria-current={currentPage === page ? "page" : false}
            tabIndex={0}
            ref={currentPage === page ? currentPageRef : null}
          >
            {page}
          </Span>
        ))
      )}
      {isMobile ? (
        <MobileButton
          aria-label={t("aria_label_go_to_next")}
          variant={currentPage !== last(pages) ? "outlined" : undefined}
          disabled={currentPage === last(pages)}
          onClick={() => handleNextClick()}
          className={focusClass.focusItem}
          aria-disabled={currentPage === last(pages)}
        >
          {t("paginator_next")}
        </MobileButton>
      ) : (
        <IconButton
          aria-label={t("aria_label_go_to_next")}
          color="primary"
          onClick={() => handleNextClick()}
          disabled={currentPage === last(pages)}
          tabIndex={0}
          className={focusClass.focusItem}
          aria-disabled={currentPage === last(pages)}
          size="large"
        >
          <SvgIcon component={arrowIcon} style={{ transform: "translate(25%, 30%)" }} />
        </IconButton>
      )}
    </Box>
  );
};
